/**
 * SearchPage.jsx
 *
 * Page de recherche avec filtres avancés
 * ✅ Bug fix : Affiche quelque chose même si URL vide
 */

import React, { useState, useEffect } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useLocation } from "react-router-dom";
import { Empty, Spin } from "antd";
import { searchCards } from "../api/api";
import CardGrid from "../components/Cards/CardGrid";
import SortBar from "../components/Search/SortBar";
import AdvancedSearchFilters from "../components/Search/AdvancedSearchFilter";
import { useTheme } from "../context/ThemeContext";

/**
 * SearchPage - Recherche de cartes
 */
export default function SearchPage() {
    const { isDarkMode } = useTheme();
    const location = useLocation();

    const [filters, setFilters] = useState({});
    const [cards, setCards] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [sortBy, setSortBy] = useState("date");
    const [order, setOrder] = useState("desc");
    const [total, setTotal] = useState(0);
    const [hydrated, setHydrated] = useState(false);
    const [loading, setLoading] = useState(false);

    // Hydrate depuis l'URL
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const newFilters = {};

        params.forEach((value, key) => {
            if (key.endsWith("[]")) {
                const cleanKey = key.replace("[]", "");
                if (!newFilters[cleanKey]) newFilters[cleanKey] = [];
                newFilters[cleanKey].push(value);
            } else {
                newFilters[key] = value;
            }
        });

        setFilters(newFilters);
        setPage(1);
        setHasMore(true);
        setHydrated(true);

        // ✅ FIX : Affiche quelque chose même si URL vide
        if (Object.keys(newFilters).length > 0) {
            fetchCards(1, newFilters, true);
        } else {
            setCards([]);
            setTotal(0);
        }
    }, [location.search]);

    // Récupère les cartes
    const fetchCards = async (pageNum = 1, appliedFilters = filters, reset = false) => {
        try {
            setLoading(true);
            const res = await searchCards(
                appliedFilters,
                pageNum,
                20,
                sortBy,
                order
            );
            const data = res.data.cards || [];

            setCards(reset || pageNum === 1 ? data : (prev) => [...prev, ...data]);
            setTotal(res.data.total || 0);
            setHasMore(data.length >= 20);
        } catch (err) {
            console.error("Erreur recherche", err);
        } finally {
            setLoading(false);
        }
    };

    const handleApplyFilters = (appliedFilters) => {
        setFilters(appliedFilters);
        setPage(1);
        setHasMore(true);
        fetchCards(1, appliedFilters, true);
    };

    // Tri et ordre
    useEffect(() => {
        if (!hydrated || Object.keys(filters).length === 0) return;
        setPage(1);
        setHasMore(true);
        fetchCards(1, filters, true);
    }, [sortBy, order]);

    const fetchMore = () => {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchCards(nextPage);
    };

    return (
        <div style={{ padding: "16px", minHeight: "100vh" }}>
            <h2 style={{ marginBottom: "16px", fontSize: "24px", fontWeight: "600" }}>
                Recherche de cartes : {total} trouvée{total > 1 ? "s" : ""}
            </h2>

            <AdvancedSearchFilters onApply={handleApplyFilters} initialFilters={filters} />

            {/* ✅ FIX : Affiche quelque chose si aucun filtre */}
            {Object.keys(filters).length === 0 ? (
                <div style={{ textAlign: "center", padding: "48px 0" }}>
                    <Empty
                        description="Utilise les filtres pour chercher des cartes"
                        style={{ opacity: 0.5 }}
                    />
                </div>
            ) : (
                <>
                    <SortBar sortBy={sortBy} setSortBy={setSortBy} order={order} setOrder={setOrder} />

                    {loading && cards.length === 0 ? (
                        <div style={{ textAlign: "center", padding: "48px" }}>
                            <Spin />
                        </div>
                    ) : (
                        <InfiniteScroll
                            dataLength={cards.length}
                            next={fetchMore}
                            hasMore={hasMore}
                            loader={
                                <h4 style={{ textAlign: "center", padding: "16px" }}>
                                    Chargement...
                                </h4>
                            }
                        >
                            <CardGrid cards={cards} />
                        </InfiniteScroll>
                    )}
                </>
            )}
        </div>
    );
}